import { useMemo, useState } from "react";
import { Phone } from "lucide-react";
import { BRAND, GALLERY } from "../data/site";

const CATEGORIES = ["All", "Kitchens", "Bathrooms", "Living Rooms"];

const Gallery = () => {
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    if (active === "All") return GALLERY;
    return GALLERY.filter((g) => g.category === active);
  }, [active]);

  return (
    <div data-testid="gallery-page" className="pt-32">
      <section className="bg-stone-50 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase text-xs tracking-[0.25em] text-stone-500 font-semibold mb-6">
            Portfolio
          </p>
          <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight text-stone-900 leading-[1.02] max-w-4xl">
            Tile. Stone. Light.
            <span className="block italic text-[#9B4819]">Our recent work.</span>
          </h1>
          <p className="mt-8 text-stone-600 text-base sm:text-lg max-w-2xl leading-relaxed">
            A selection of kitchen, bathroom, and living-room remodels completed
            across the Pacific Northwest.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-stone-50 pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-center gap-3 overflow-x-auto no-scrollbar border-t border-stone-200 pt-8">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                data-testid={`gallery-filter-${cat.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-5 py-2.5 text-sm tracking-wide border transition-all duration-300 whitespace-nowrap ${
                  active === cat
                    ? "bg-stone-900 text-white border-stone-900"
                    : "bg-transparent text-stone-700 border-stone-300 hover:border-stone-900 hover:text-stone-900"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-stone-50 pb-28 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-12 gap-5">
            {filtered.map((img, idx) => {
              // Varied grid: certain indexes span wider / taller for visual rhythm
              const pattern = [
                "col-span-12 md:col-span-8 h-[420px] md:h-[520px]",
                "col-span-12 md:col-span-4 h-[420px] md:h-[520px]",
                "col-span-6 md:col-span-4 h-[300px] md:h-[360px]",
                "col-span-6 md:col-span-4 h-[300px] md:h-[360px]",
                "col-span-12 md:col-span-4 h-[300px] md:h-[360px]",
                "col-span-12 md:col-span-6 h-[360px] md:h-[420px]",
                "col-span-12 md:col-span-6 h-[360px] md:h-[420px]",
              ];
              const cls = pattern[idx % pattern.length];
              return (
                <figure
                  key={img.id}
                  data-testid={`gallery-item-${img.id}`}
                  className={`group overflow-hidden ${cls}`}
                >
                  <div className="relative w-full h-full">
                    <img
                      src={img.src}
                      alt={img.title}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                    <figcaption className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-stone-950/80 to-transparent text-white">
                      <p className="uppercase text-[10px] tracking-[0.25em] text-[#E8A976] mb-1">
                        {img.category}
                      </p>
                      <p className="text-sm font-medium">{img.title}</p>
                    </figcaption>
                  </div>
                </figure>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <p className="text-center text-stone-500 py-20">
              No projects in this category yet. Check back soon.
            </p>
          )}
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-stone-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-display font-light text-3xl sm:text-4xl leading-tight max-w-2xl">
            Like what you see?
            <span className="block italic text-[#E8A976]">Let's build yours.</span>
          </h2>
          <a
            href={BRAND.phoneLink}
            data-testid="gallery-phone-cta"
            className="inline-flex items-center gap-3 bg-[#9B4819] hover:bg-[#7A3813] text-white px-8 py-5 text-base font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
          >
            <Phone className="w-5 h-5" />
            {BRAND.phoneDisplay}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
